import React, { useEffect, useContext, useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  GraduationCap, 
  BookOpen, 
  FileText, 
  Mail, 
  Phone, 
  MapPin, 
  Calendar, 
  Award, 
  ArrowLeft, 
  ArrowRight, 
  ChevronRight, 
  Sparkles, 
  Users, 
  CheckCircle2, 
  Library, 
  Bookmark,
  Share2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { residentialFacultyList } from '../../../ProData';
import { ProductContext } from '../../../ProductProvider';

export default function FacultyTemplate() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { facultyDetails, setFacultyDetails } = useContext(ProductContext);
  const [activeTab, setActiveTab] = useState('qualifications');

  // Resolve current faculty from route param or context fallback
  const faculty = useMemo(() => {
    if (id) {
      const found = residentialFacultyList.find((item) => item.id === parseInt(id));
      if (found) return found;
    }
    if (facultyDetails && facultyDetails.id) {
      return facultyDetails;
    }
    return residentialFacultyList[0];
  }, [id, facultyDetails]);

  // Sync context and scroll on load
  useEffect(() => {
    window.scrollTo(0, 0);
    if (faculty && (!facultyDetails || facultyDetails.id !== faculty.id)) {
      setFacultyDetails(faculty);
    }
  }, [faculty]);

  // Previous and Next faculty for seamless browsing
  const currentIndex = useMemo(() => {
    return residentialFacultyList.findIndex((f) => f.id === faculty.id);
  }, [faculty]);

  const prevFaculty = useMemo(() => {
    if (currentIndex <= 0) return residentialFacultyList[residentialFacultyList.length - 1];
    return residentialFacultyList[currentIndex - 1];
  }, [currentIndex]);

  const nextFaculty = useMemo(() => {
    if (currentIndex >= residentialFacultyList.length - 1) return residentialFacultyList[0];
    return residentialFacultyList[currentIndex + 1];
  }, [currentIndex]);

  // Publication statistics
  const quals = faculty.academicQualification || [];
  const books = faculty.booksPublished || [];
  const editedVols = faculty.editedVolumes || [];
  const manuscripts = faculty.manuscript || [];
  const researchArticles = faculty.researchArticles || [];
  const generalArticles = faculty.generalArticles || [];
  const bookReviews = faculty.bookReviews || [];
  const editorials = faculty.editorials || [];
  const prevTeachings = faculty.previousTeaching || [];

  const totalBooks = books.length + editedVols.length + manuscripts.length;
  const totalArticles = researchArticles.length + generalArticles.length + bookReviews.length + editorials.length;
  const totalPubs = totalBooks + totalArticles;

  const isLeadership = (faculty.title || '').toLowerCase().includes('principal') || 
                       (faculty.title || '').toLowerCase().includes('registrar') || 
                       (faculty.title || '').toLowerCase().includes('director') || 
                       (faculty.title || '').toLowerCase().includes('editor');

  return (
    <div className="vj-faculty-profile-page py-5" style={{ background: '#f8fafc', minHeight: '100vh' }}>
      <Helmet>
        <title>{faculty.name} - Academic Profile | Vidyajyoti College of Theology</title>
        <meta 
          name="description" 
          content={`Academic profile and curriculum vitae of ${faculty.name}, ${faculty.title || faculty.category} at Vidyajyoti College of Theology, Delhi.`} 
        />
        <meta name="keywords" content={`${faculty.name}, Vidyajyoti Faculty, Jesuit Theologian, Catholic Theology Delhi`} />
        <link rel="canonical" href={`/residentialfaculty/cv/${faculty.id}`} />
      </Helmet>

      <div className="container">
        {/* Navigation Breadcrumb & Back Bar */}
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-2 border-bottom">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0 small">
              <li className="breadcrumb-item">
                <Link to="/" className="text-decoration-none text-muted">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/about/residentialFaculty" className="text-decoration-none text-muted">Residential Faculty</Link>
              </li>
              <li className="breadcrumb-item active text-dark fw-semibold" aria-current="page">
                {faculty.name}
              </li>
            </ol>
          </nav>

          <div className="d-flex align-items-center gap-2">
            <Link
              to="/about/residentialFaculty"
              className="btn btn-sm rounded-pill px-3 py-1.5 fw-semibold d-inline-flex align-items-center gap-1.5"
              style={{ background: '#ffffff', border: '1px solid rgba(26,43,86,0.15)', color: '#1a2b56' }}
            >
              <ArrowLeft size={14} /> Back to Faculty List
            </Link>
          </div>
        </div>

        {/* Hero Profile Card */}
        <motion.div
          className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-5"
          style={{
            borderTop: isLeadership ? '5px solid #79031d' : '5px solid #1a2b56'
          }}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="row align-items-center g-4">
            {/* Portrait Photo */}
            <div className="col-lg-3 col-md-4 text-center">
              <div className="position-relative d-inline-block">
                <div
                  className="rounded-circle p-1.5 shadow-sm"
                  style={{
                    background: isLeadership 
                      ? 'linear-gradient(135deg, #79031d 0%, #c69214 100%)' 
                      : 'linear-gradient(135deg, #1a2b56 0%, #c69214 100%)',
                    width: '180px',
                    height: '180px',
                    margin: '0 auto'
                  }}
                >
                  <img
                    src={faculty.avatarSrc}
                    alt={faculty.name}
                    className="rounded-circle w-100 h-100 shadow-inner"
                    style={{
                      objectFit: 'cover',
                      background: '#f1f5f9'
                    }}
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Profile Info Details */}
            <div className="col-lg-9 col-md-8">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                {faculty.title && (
                  <span
                    className="badge rounded-pill px-3 py-1.5"
                    style={{
                      background: isLeadership ? 'rgba(121, 3, 29, 0.1)' : 'rgba(26, 43, 86, 0.08)',
                      color: isLeadership ? '#79031d' : '#1a2b56',
                      fontWeight: 600,
                      fontSize: '0.82rem'
                    }}
                  >
                    <Award size={13} className="me-1 inline-block" />
                    {faculty.title}
                  </span>
                )}

                {faculty.category && (
                  <span
                    className="badge rounded-pill px-3 py-1.5 bg-light text-secondary border"
                    style={{ fontSize: '0.82rem', fontWeight: 500 }}
                  >
                    <BookOpen size={13} className="me-1 inline-block" />
                    {faculty.category}
                  </span>
                )}
              </div>

              <h1 
                className="fw-bold mb-2" 
                style={{ color: '#1a2b56', fontSize: '1.95rem', fontFamily: "'Outfit', sans-serif" }}
              >
                {faculty.name}
              </h1>

              {faculty.subTitle && (
                <p className="text-muted fw-semibold mb-3" style={{ fontSize: '1rem' }}>
                  {faculty.subTitle}
                </p>
              )}

              {/* Key Details Metadata Grid */}
              <div className="row g-2 mt-2 pt-2 border-top">
                {faculty.emailID && faculty.emailID.trim() !== '' && faculty.emailID !== '-' && (
                  <div className="col-sm-6 col-lg-4">
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <div className="p-1.5 rounded-circle bg-light text-primary">
                        <Mail size={14} />
                      </div>
                      <div>
                        <span className="d-block text-muted" style={{ fontSize: '0.72rem' }}>Email Address</span>
                        <a 
                          href={`mailto:${faculty.emailID}`} 
                          className="fw-semibold text-dark text-decoration-none"
                          style={{ wordBreak: 'break-all' }}
                        >
                          {faculty.emailID}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {faculty.province && faculty.province.trim() !== '' && (
                  <div className="col-sm-6 col-lg-4">
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <div className="p-1.5 rounded-circle bg-light text-burgundy" style={{ color: '#79031d' }}>
                        <MapPin size={14} />
                      </div>
                      <div>
                        <span className="d-block text-muted" style={{ fontSize: '0.72rem' }}>Jesuit Province</span>
                        <span className="fw-semibold text-dark">{faculty.province}</span>
                      </div>
                    </div>
                  </div>
                )}

                {faculty.contact && faculty.contact.trim() !== '' && (
                  <div className="col-sm-6 col-lg-4">
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <div className="p-1.5 rounded-circle bg-light text-success">
                        <Phone size={14} />
                      </div>
                      <div>
                        <span className="d-block text-muted" style={{ fontSize: '0.72rem' }}>Contact Phone</span>
                        <a href={`tel:${faculty.contact}`} className="fw-semibold text-dark text-decoration-none">
                          {faculty.contact}
                        </a>
                      </div>
                    </div>
                  </div>
                )}

                {faculty.dob && faculty.dob.trim() !== '' && (
                  <div className="col-sm-6 col-lg-4">
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <div className="p-1.5 rounded-circle bg-light text-secondary">
                        <Calendar size={14} />
                      </div>
                      <div>
                        <span className="d-block text-muted" style={{ fontSize: '0.72rem' }}>Date of Birth</span>
                        <span className="fw-semibold text-dark">{faculty.dob}</span>
                      </div>
                    </div>
                  </div>
                )}

                {faculty.pob && faculty.pob.trim() !== '' && (
                  <div className="col-sm-6 col-lg-4">
                    <div className="d-flex align-items-center gap-2 text-muted small">
                      <div className="p-1.5 rounded-circle bg-light text-secondary">
                        <MapPin size={14} />
                      </div>
                      <div>
                        <span className="d-block text-muted" style={{ fontSize: '0.72rem' }}>Place of Birth</span>
                        <span className="fw-semibold text-dark">{faculty.pob}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Metrics Bar */}
          <div className="row g-2 text-center mt-4 pt-4 border-top">
            <div className="col-6 col-md-3 border-end">
              <div className="p-2">
                <span className="fw-bold fs-4 d-block" style={{ color: '#1a2b56' }}>{quals.length}</span>
                <small className="text-muted fw-semibold">Qualifications &amp; Degrees</small>
              </div>
            </div>
            <div className="col-6 col-md-3 border-end">
              <div className="p-2">
                <span className="fw-bold fs-4 d-block" style={{ color: '#79031d' }}>{totalBooks}</span>
                <small className="text-muted fw-semibold">Books &amp; Volumes</small>
              </div>
            </div>
            <div className="col-6 col-md-3 border-end">
              <div className="p-2">
                <span className="fw-bold fs-4 d-block" style={{ color: '#c69214' }}>{totalArticles}</span>
                <small className="text-muted fw-semibold">Articles &amp; Papers</small>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="p-2">
                <span className="fw-bold fs-4 d-block" style={{ color: '#0d6efd' }}>
                  {(faculty.currentTeaching ? 1 : 0) + prevTeachings.length}
                </span>
                <small className="text-muted fw-semibold">Teaching Domains</small>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Dossier Navigation Tabs */}
        <div className="d-flex flex-wrap gap-2 mb-4">
          <button
            type="button"
            onClick={() => setActiveTab('qualifications')}
            className={`btn rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 transition-all ${
              activeTab === 'qualifications' ? 'btn-dark shadow-sm' : 'btn-outline-secondary bg-white'
            }`}
            style={{
              background: activeTab === 'qualifications' ? '#1a2b56' : '#ffffff',
              borderColor: activeTab === 'qualifications' ? '#1a2b56' : 'rgba(26,43,86,0.15)',
              color: activeTab === 'qualifications' ? '#ffffff' : '#475569'
            }}
          >
            <GraduationCap size={16} />
            <span>Academic Qualifications</span>
            <span className={`badge rounded-pill ms-1 ${activeTab === 'qualifications' ? 'bg-light text-dark' : 'bg-secondary text-white'}`}>
              {quals.length}
            </span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('teaching')}
            className={`btn rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 transition-all ${
              activeTab === 'teaching' ? 'btn-dark shadow-sm' : 'btn-outline-secondary bg-white'
            }`}
            style={{
              background: activeTab === 'teaching' ? '#1a2b56' : '#ffffff',
              borderColor: activeTab === 'teaching' ? '#1a2b56' : 'rgba(26,43,86,0.15)',
              color: activeTab === 'teaching' ? '#ffffff' : '#475569'
            }}
          >
            <BookOpen size={16} />
            <span>Teaching &amp; Praxis</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('publications')}
            className={`btn rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 transition-all ${
              activeTab === 'publications' ? 'btn-dark shadow-sm' : 'btn-outline-secondary bg-white'
            }`}
            style={{
              background: activeTab === 'publications' ? '#1a2b56' : '#ffffff',
              borderColor: activeTab === 'publications' ? '#1a2b56' : 'rgba(26,43,86,0.15)',
              color: activeTab === 'publications' ? '#ffffff' : '#475569'
            }}
          >
            <Library size={16} />
            <span>Publications &amp; Research</span>
            {totalPubs > 0 && (
              <span className={`badge rounded-pill ms-1 ${activeTab === 'publications' ? 'bg-light text-dark' : 'bg-secondary text-white'}`}>
                {totalPubs}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('contact')}
            className={`btn rounded-pill px-4 py-2 fw-semibold d-inline-flex align-items-center gap-2 transition-all ${
              activeTab === 'contact' ? 'btn-dark shadow-sm' : 'btn-outline-secondary bg-white'
            }`}
            style={{
              background: activeTab === 'contact' ? '#1a2b56' : '#ffffff',
              borderColor: activeTab === 'contact' ? '#1a2b56' : 'rgba(26,43,86,0.15)',
              color: activeTab === 'contact' ? '#ffffff' : '#475569'
            }}
          >
            <Mail size={16} />
            <span>Contact &amp; Mentorship</span>
          </button>
        </div>

        {/* Tab Content Display */}
        <AnimatePresence mode="wait">
          {activeTab === 'qualifications' && (
            <motion.div
              key="qualifications"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4 pb-3 border-bottom">
                  <div className="d-flex align-items-center gap-3">
                    <div 
                      className="p-3 rounded-4 shadow-sm text-white d-flex align-items-center justify-content-center"
                      style={{ background: 'linear-gradient(135deg, #1a2b56 0%, #79031d 100%)', width: '52px', height: '52px' }}
                    >
                      <GraduationCap size={26} />
                    </div>
                    <div>
                      <span className="badge px-3 py-1 rounded-pill mb-1" style={{ background: 'rgba(26,43,86,0.08)', color: '#1a2b56', fontWeight: 600 }}>
                        SCHOLARLY FORMATION &amp; DEGREES
                      </span>
                      <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.45rem', fontFamily: "'Outfit', sans-serif" }}>
                        Academic Qualifications &amp; Theological Credentials
                      </h3>
                    </div>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    <span className="badge rounded-pill bg-light text-dark border px-3 py-2 fw-semibold">
                      🎓 {quals.length} Verified Degrees
                    </span>
                  </div>
                </div>

                {quals.length > 0 ? (
                  <div className="d-flex flex-column gap-3.5">
                    {quals.map((qual, idx) => {
                      const degreeText = qual.degree || '';
                      const collegeText = qual.college || '';
                      const yearText = qual.year || '';
                      const combined = `${degreeText} ${collegeText}`.toLowerCase();

                      // Tier categorization & styling
                      let tier = {
                        name: 'Academic Degree',
                        badgeColor: '#1a2b56',
                        badgeBg: 'rgba(26, 43, 86, 0.08)',
                        accentColor: '#1a2b56',
                        icon: GraduationCap
                      };

                      if (combined.includes('doctor') || combined.includes('ph.d') || combined.includes('th.d') || combined.includes('std')) {
                        tier = {
                          name: 'Doctoral Degree (Ph.D. / Th.D. / STD)',
                          badgeColor: '#79031d',
                          badgeBg: 'rgba(121, 3, 29, 0.1)',
                          accentColor: '#79031d',
                          icon: Award
                        };
                      } else if (combined.includes('licentiate') || combined.includes('stl') || combined.includes('ssl')) {
                        tier = {
                          name: 'Pontifical Licentiate (STL / SSL)',
                          badgeColor: '#c69214',
                          badgeBg: 'rgba(198, 146, 20, 0.15)',
                          accentColor: '#c69214',
                          icon: Sparkles
                        };
                      } else if (combined.includes('master') || combined.includes('m.a') || combined.includes('ma ') || combined.includes('m.sc') || combined.includes('m.th') || combined.includes('m.phil') || combined.includes('m.div') || combined.includes('mba') || combined.includes('post-graduation')) {
                        tier = {
                          name: 'Master’s Degree / Post-Graduate',
                          badgeColor: '#1a2b56',
                          badgeBg: 'rgba(26, 43, 86, 0.08)',
                          accentColor: '#1a2b56',
                          icon: BookOpen
                        };
                      } else if (combined.includes('bachelor') || combined.includes('b.a') || combined.includes('ba ') || combined.includes('b.sc') || combined.includes('b.th') || combined.includes('b.ph') || combined.includes('b.ed') || combined.includes('bst') || combined.includes('stb') || combined.includes('graduation')) {
                        tier = {
                          name: 'Bachelor’s Degree / Undergraduate',
                          badgeColor: '#0284c7',
                          badgeBg: 'rgba(2, 132, 199, 0.08)',
                          accentColor: '#0284c7',
                          icon: GraduationCap
                        };
                      } else if (combined.includes('diploma')) {
                        tier = {
                          name: 'Diploma / Specialization',
                          badgeColor: '#475569',
                          badgeBg: 'rgba(71, 85, 105, 0.08)',
                          accentColor: '#475569',
                          icon: FileText
                        };
                      }

                      const TierIcon = tier.icon;
                      const hasGoldMedal = combined.includes('gold medallist') || combined.includes('gold medalist') || combined.includes('gold medal');
                      const hasDistinction = combined.includes('distinction');

                      return (
                        <div
                          key={idx}
                          className="p-4 rounded-4 bg-white border shadow-sm transition-all"
                          style={{
                            borderLeft: `5px solid ${tier.accentColor}`,
                            background: '#ffffff'
                          }}
                        >
                          <div className="row align-items-center g-3">
                            {/* Stepper badge & icon */}
                            <div className="col-auto d-none d-sm-block">
                              <div
                                className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
                                style={{
                                  width: '46px',
                                  height: '46px',
                                  background: `linear-gradient(135deg, ${tier.accentColor} 0%, #1a2b56 100%)`,
                                  fontSize: '0.9rem'
                                }}
                              >
                                {idx < 9 ? `0${idx + 1}` : idx + 1}
                              </div>
                            </div>

                            {/* Degree Details */}
                            <div className="col">
                              {/* Badges Header */}
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-1.5">
                                <span
                                  className="badge rounded-pill px-3 py-1"
                                  style={{
                                    background: tier.badgeBg,
                                    color: tier.badgeColor,
                                    fontSize: '0.78rem',
                                    fontWeight: 600,
                                    border: `1px solid ${tier.badgeColor}33`
                                  }}
                                >
                                  <TierIcon size={12} className="me-1 inline-block" />
                                  {tier.name}
                                </span>

                                {yearText && yearText.trim() !== '' && (
                                  <span className="badge rounded-pill bg-light text-dark border px-2.5 py-1" style={{ fontSize: '0.78rem' }}>
                                    <Calendar size={12} className="me-1 inline-block text-secondary" />
                                    {yearText}
                                  </span>
                                )}

                                {hasGoldMedal && (
                                  <span
                                    className="badge rounded-pill px-2.5 py-1 text-white shadow-sm"
                                    style={{ background: 'linear-gradient(135deg, #c69214 0%, #f59e0b 100%)', fontSize: '0.78rem' }}
                                  >
                                    🥇 Gold Medallist
                                  </span>
                                )}

                                {hasDistinction && (
                                  <span
                                    className="badge rounded-pill px-2.5 py-1"
                                    style={{ background: 'rgba(198, 146, 20, 0.15)', color: '#8a6200', border: '1px solid rgba(198, 146, 20, 0.3)', fontSize: '0.78rem' }}
                                  >
                                    🌟 Distinction
                                  </span>
                                )}
                              </div>

                              {/* Degree Title */}
                              <h4 
                                className="fw-bold mb-1 mt-1" 
                                style={{ color: '#1a2b56', fontSize: '1.2rem', fontFamily: "'Outfit', sans-serif" }}
                              >
                                {degreeText}
                              </h4>

                              {/* University / College Callout */}
                              {collegeText && collegeText.trim() !== '' && (
                                <div className="d-flex align-items-center gap-2 mt-2 text-muted small">
                                  <div className="p-1 rounded-circle bg-light text-primary flex-shrink-0">
                                    <Library size={14} />
                                  </div>
                                  <span className="text-dark fw-medium" style={{ fontSize: '0.94rem' }}>
                                    {collegeText}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <GraduationCap size={36} className="mb-2 text-secondary opacity-50" />
                    <p className="mb-0">Academic qualifications are currently being updated.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'teaching' && (
            <motion.div
              key="teaching"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white mb-4">
                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                  <div className="p-2 rounded-3 bg-light text-burgundy" style={{ color: '#79031d' }}>
                    <BookOpen size={22} />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                      Teaching Domains &amp; Pedagogical Praxis
                    </h3>
                    <small className="text-muted">Courses taught, student mentorship, and pastoral theological immersion</small>
                  </div>
                </div>

                {/* Current Teaching */}
                <div className="mb-4">
                  <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(121, 3, 29, 0.1)', color: '#79031d' }}>
                    Current Academic Coursework
                  </span>
                  <div className="p-4 rounded-4 bg-light border" style={{ borderLeft: '4px solid #79031d' }}>
                    <h5 className="fw-bold mb-2" style={{ color: '#79031d' }}>
                      {faculty.currentTeaching || faculty.category || 'Theological Courses'}
                    </h5>
                    <p className="text-muted mb-0 small" style={{ lineHeight: '1.7' }}>
                      Facilitating foundational and advanced seminars for B.Th., M.Th., and doctoral students at Vidyajyoti Faculty of Theology, Delhi.
                    </p>
                  </div>
                </div>

                {/* Previous Teaching */}
                {prevTeachings.length > 0 && (
                  <div>
                    <span className="badge px-3 py-1 rounded-pill mb-2" style={{ background: 'rgba(26, 43, 86, 0.08)', color: '#1a2b56' }}>
                      Previous Teaching &amp; Institutional Appointments
                    </span>
                    <div className="row g-3">
                      {prevTeachings.map((pt, idx) => (
                        <div key={idx} className="col-md-6">
                          <div className="p-3.5 rounded-4 h-100 border bg-white shadow-sm" style={{ borderLeft: '4px solid #c69214' }}>
                            <div className="d-flex justify-content-between align-items-start gap-2 mb-1">
                              <h6 className="fw-bold mb-1" style={{ color: '#1a2b56' }}>
                                {pt.details}
                              </h6>
                              {pt.year && pt.year.trim() !== '' && (
                                <span className="badge bg-light text-dark border rounded-pill">
                                  {pt.year}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'publications' && (
            <motion.div
              key="publications"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white">
                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                  <div className="p-2 rounded-3 bg-light text-success">
                    <Library size={22} />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                      Scholarly Output &amp; Publications
                    </h3>
                    <small className="text-muted">Authored books, edited anthologies, peer-reviewed articles, and critical reviews</small>
                  </div>
                </div>

                {totalPubs > 0 ? (
                  <div className="d-flex flex-column gap-4">
                    {/* Books Published */}
                    {books.length > 0 && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <Bookmark size={18} style={{ color: '#79031d' }} />
                          <h4 className="fw-bold mb-0 fs-5" style={{ color: '#79031d' }}>
                            Authored Books ({books.length})
                          </h4>
                        </div>
                        <div className="row g-3">
                          {books.map((b, idx) => (
                            <div key={idx} className="col-12">
                              <div className="p-3.5 rounded-4 border bg-light shadow-sm d-flex align-items-start gap-3">
                                <span className="badge rounded-circle p-2 bg-white text-dark border fw-bold">
                                  {idx + 1}
                                </span>
                                <p className="mb-0 text-dark fw-medium" style={{ lineHeight: '1.7', fontSize: '0.96rem' }}>
                                  {b.details}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Edited Volumes */}
                    {editedVols.length > 0 && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <Bookmark size={18} style={{ color: '#c69214' }} />
                          <h4 className="fw-bold mb-0 fs-5" style={{ color: '#c69214' }}>
                            Edited Volumes ({editedVols.length})
                          </h4>
                        </div>
                        <div className="row g-3">
                          {editedVols.map((ev, idx) => (
                            <div key={idx} className="col-12">
                              <div className="p-3.5 rounded-4 border bg-light shadow-sm d-flex align-items-start gap-3">
                                <span className="badge rounded-circle p-2 bg-white text-dark border fw-bold">
                                  {idx + 1}
                                </span>
                                <p className="mb-0 text-dark fw-medium" style={{ lineHeight: '1.7', fontSize: '0.96rem' }}>
                                  {ev.details}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Research Articles */}
                    {researchArticles.length > 0 && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <FileText size={18} style={{ color: '#1a2b56' }} />
                          <h4 className="fw-bold mb-0 fs-5" style={{ color: '#1a2b56' }}>
                            Peer-Reviewed Research Articles ({researchArticles.length})
                          </h4>
                        </div>
                        <div className="row g-3">
                          {researchArticles.map((ra, idx) => (
                            <div key={idx} className="col-12">
                              <div className="p-3.5 rounded-4 border bg-white shadow-sm d-flex align-items-start gap-3">
                                <span className="badge rounded-circle p-2 bg-light text-dark border fw-bold">
                                  {idx + 1}
                                </span>
                                <p className="mb-0 text-dark" style={{ lineHeight: '1.7', fontSize: '0.95rem' }}>
                                  {ra.details}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* General Articles */}
                    {generalArticles.length > 0 && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <FileText size={18} className="text-secondary" />
                          <h4 className="fw-bold mb-0 fs-5 text-dark">
                            General Essays &amp; Articles ({generalArticles.length})
                          </h4>
                        </div>
                        <div className="row g-3">
                          {generalArticles.map((ga, idx) => (
                            <div key={idx} className="col-12">
                              <div className="p-3.5 rounded-4 border bg-white shadow-sm d-flex align-items-start gap-3">
                                <span className="badge rounded-circle p-2 bg-light text-dark border fw-bold">
                                  {idx + 1}
                                </span>
                                <p className="mb-0 text-dark" style={{ lineHeight: '1.7', fontSize: '0.95rem' }}>
                                  {ga.details}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Book Reviews */}
                    {bookReviews.length > 0 && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <FileText size={18} className="text-muted" />
                          <h4 className="fw-bold mb-0 fs-5 text-dark">
                            Book Reviews ({bookReviews.length})
                          </h4>
                        </div>
                        <div className="row g-3">
                          {bookReviews.map((br, idx) => (
                            <div key={idx} className="col-12">
                              <div className="p-3.5 rounded-4 border bg-white shadow-sm d-flex align-items-start gap-3">
                                <span className="badge rounded-circle p-2 bg-light text-dark border fw-bold">
                                  {idx + 1}
                                </span>
                                <p className="mb-0 text-dark" style={{ lineHeight: '1.7', fontSize: '0.95rem' }}>
                                  {br.details}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Editorials */}
                    {editorials.length > 0 && (
                      <div>
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <FileText size={18} style={{ color: '#79031d' }} />
                          <h4 className="fw-bold mb-0 fs-5" style={{ color: '#79031d' }}>
                            Editorials ({editorials.length})
                          </h4>
                        </div>
                        <div className="row g-3">
                          {editorials.map((ed, idx) => (
                            <div key={idx} className="col-12">
                              <div className="p-3.5 rounded-4 border bg-white shadow-sm d-flex align-items-start gap-3">
                                <span className="badge rounded-circle p-2 bg-light text-dark border fw-bold">
                                  {idx + 1}
                                </span>
                                <p className="mb-0 text-dark" style={{ lineHeight: '1.7', fontSize: '0.95rem' }}>
                                  {ed.details}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-5 text-muted">
                    <Library size={36} className="mb-2 text-secondary opacity-50" />
                    <p className="mb-0">Publications list is currently being cataloged.</p>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {activeTab === 'contact' && (
            <motion.div
              key="contact"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.25 }}
            >
              <div className="card border-0 rounded-4 shadow-sm p-4 p-md-5 bg-white">
                <div className="d-flex align-items-center gap-2 mb-4 pb-2 border-bottom">
                  <div className="p-2 rounded-3 bg-light text-primary">
                    <Mail size={22} />
                  </div>
                  <div>
                    <h3 className="fw-bold mb-0" style={{ color: '#1a2b56', fontSize: '1.4rem' }}>
                      Academic Contact &amp; Student Mentorship
                    </h3>
                    <small className="text-muted">Office consultation hours and research correspondence</small>
                  </div>
                </div>

                <div className="row g-4">
                  <div className="col-lg-7">
                    <div className="p-4 rounded-4 bg-light border mb-4">
                      <h5 className="fw-bold mb-3" style={{ color: '#1a2b56' }}>
                        Institutional Office
                      </h5>
                      <div className="d-flex flex-column gap-2 text-muted small">
                        <p className="mb-1">
                          <strong>Vidyajyoti College of Theology</strong><br />
                          4A, Raj Niwas Marg, Civil Lines,<br />
                          Delhi - 110054, India
                        </p>
                        {faculty.emailID && (
                          <p className="mb-1">
                            <strong>Email:</strong>{' '}
                            <a href={`mailto:${faculty.emailID}`} className="text-decoration-none">
                              {faculty.emailID}
                            </a>
                          </p>
                        )}
                        {faculty.province && (
                          <p className="mb-0">
                            <strong>Jesuit Province:</strong> {faculty.province}
                          </p>
                        )}
                      </div>
                    </div>

                    {faculty.emailID && (
                      <div>
                        <a
                          href={`mailto:${faculty.emailID}`}
                          className="btn text-white rounded-pill px-4 py-2.5 fw-semibold d-inline-flex align-items-center gap-2 shadow-sm"
                          style={{ background: '#1a2b56' }}
                        >
                          <Mail size={16} /> Send Academic Email
                        </a>
                      </div>
                    )}
                  </div>

                  <div className="col-lg-5">
                    <div className="p-4 rounded-4 border bg-white shadow-sm" style={{ borderLeft: '4px solid #c69214' }}>
                      <h6 className="fw-bold mb-2" style={{ color: '#1a2b56' }}>
                        Research Consultation
                      </h6>
                      <p className="text-muted small mb-3" style={{ lineHeight: '1.6' }}>
                        Students pursuing B.Th. comprehensive papers, M.Th. theses, or Ph.D. dissertations may schedule office consultations by email appointment.
                      </p>
                      <div className="d-flex align-items-center gap-2 text-dark small fw-semibold">
                        <CheckCircle2 size={16} className="text-success" />
                        <span>Academic Mentoring Available</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Quick Switcher Between Faculty Members */}
        <div className="card border-0 rounded-4 shadow-sm p-4 bg-white mt-5">
          <div className="row align-items-center justify-content-between g-3">
            <div className="col-md-5">
              <Link
                to={`/residentialfaculty/cv/${prevFaculty.id}`}
                className="text-decoration-none d-flex align-items-center gap-3 p-2 rounded-3 hover-bg-light transition-all text-dark"
              >
                <div className="p-2 rounded-circle bg-light text-secondary">
                  <ArrowLeft size={18} />
                </div>
                <div>
                  <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>Previous Professor</span>
                  <strong className="small" style={{ color: '#1a2b56' }}>{prevFaculty.name}</strong>
                </div>
              </Link>
            </div>

            <div className="col-md-2 text-center border-start border-end d-none d-md-block">
              <Link
                to="/about/residentialFaculty"
                className="btn btn-sm btn-light rounded-pill px-3 py-1.5 fw-semibold text-secondary"
              >
                All Faculty
              </Link>
            </div>

            <div className="col-md-5 text-md-end">
              <Link
                to={`/residentialfaculty/cv/${nextFaculty.id}`}
                className="text-decoration-none d-flex align-items-center justify-content-md-end gap-3 p-2 rounded-3 hover-bg-light transition-all text-dark"
              >
                <div className="text-md-end">
                  <span className="d-block text-muted" style={{ fontSize: '0.75rem' }}>Next Professor</span>
                  <strong className="small" style={{ color: '#1a2b56' }}>{nextFaculty.name}</strong>
                </div>
                <div className="p-2 rounded-circle bg-light text-secondary">
                  <ArrowRight size={18} />
                </div>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

